import React, { useEffect, useRef, useState } from 'react';
import { API } from '../api';
import PulseLoader from 'react-spinners/PulseLoader';
import SearchIcon from '../assets/images/icons/svg/SearchIcon';
import { FaChevronDown } from 'react-icons/fa';

const CertifiedMembersPage = () => {
  const ref = useRef(null);
  const [certificates, setCertificates] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');

  useEffect(() => {
    const fetchCertificates = async () => {
      try {
        setIsLoading(true);
        const response = await API.get("/certificate/findAllCertificates");
        if(response?.data?.data && response?.data?.data.length) {
          setCertificates(response.data.data);
        }else{
          throw new Error('No certificates found');
        }
      } catch (error) {
        console.error('Error fetching certificates:', error);
        alert('Error fetching certificates');
      }
      finally {
        setIsLoading(false);
      }
    };
    fetchCertificates();
  },[]);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
    setCurrentPage(1); // Reset to the first page on new search
  };

  const handleCategoryChange = (event) => {
    setCategoryFilter(event.target.value);
    setCurrentPage(1); // Reset to the first page on new category filter
  };

  const filteredCertificates = certificates.filter(certificate =>
    (certificate.certificateOwner.toLowerCase().includes(searchQuery.toLowerCase()) ||
      certificate.ownerRegNumber.toLowerCase().includes(searchQuery.toLowerCase())) &&
    (categoryFilter === '' || certificate.ownerCategory === categoryFilter)
  );

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentCertificates = filteredCertificates.slice(indexOfFirstItem, indexOfLastItem);

  const renderPagination = () => {
    const pageNumbers = [];
    const totalPages = Math.ceil(filteredCertificates.length / itemsPerPage);
    const maxPageNumbersToShow = 5;

    if (totalPages <= maxPageNumbersToShow) {
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i);
      }
    } else {
      let startPage = Math.max(1, currentPage - 2);
      let endPage = Math.min(totalPages, currentPage + 2);

      if (currentPage <= 3) {
        endPage = maxPageNumbersToShow;
      } else if (currentPage + 2 >= totalPages) {
        startPage = totalPages - maxPageNumbersToShow + 1;
      }

      for (let i = startPage; i <= endPage; i++) {
        pageNumbers.push(i);
      }

      if (startPage > 1) {
        pageNumbers.unshift('...');
        pageNumbers.unshift(1);
      }

      if (endPage < totalPages) {
        pageNumbers.push('...');
        pageNumbers.push(totalPages);
      }
    }

    return (
      <div className="flex justify-center flex-wrap my-4">
        <button
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className="px-3 py-1 mx-1 bg-white text-green disabled:text-gray-101"
        >
          Prev
        </button>
        {pageNumbers.map((number, index) => (
          <button
            key={index}
            onClick={() => number !== '...' && handlePageChange(number)}
            className={`px-3 py-1 mx-1 ${currentPage === number ? 'bg-green text-white' : 'bg-white text-green'}`}
            disabled={number === '...'}
          >
            {number}
          </button>
        ))}
        <button
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className="px-3 py-1 mx-1 bg-white text-green disabled:text-gray-101"
        >
          Next
        </button>
      </div>
    );
  };


  return (
    <div className="max-w-7xl mx-auto px-8" ref={ref}>
      <h1 className="text-3xl font-bold text-center mt-10">Certified Members</h1>

      <div className="flex items-center flex-col md:flex-row gap-3 md:gap-5 my-5">
        <div className="w-full md:w-auto relative">
          <select value={categoryFilter} onChange={handleCategoryChange} className="custom-select outline-none p-3 border border-brand-green rounded-lg w-full min-w-[150px]">
            <option value="">All</option>
            <option value="Junior">Junior</option>
            <option value="Professional">Professional</option>
            <option value="Consulting">Consulting</option>
            <option value="Company">Company</option>
          </select>
          <FaChevronDown className="absolute top-4 right-3 text-brand-green"/>
        </div>

        <div className="flex justify-center border border-brand-green rounded-lg overflow-hidden w-full">
          <div className="flex items-center h-full py-3 pl-4">
            <SearchIcon />
          </div>
          <input
            type="text"
            placeholder="Search by name or reg number"
            value={searchQuery}
            onChange={handleSearchChange}
            className="px-4 py-2 border-0 outline-none w-full"
          />
          <button className="bg-brand-green text-white px-8 hidden md:block">
              Search
          </button>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full border-collapse mt-2 divide-white divide-y-[20px]">
          <thead>
          <tr className="bg-green text-white whitespace-nowrap">
            <th className="p-4 text-left">License Nbr</th>
            <th className="p-4 text-left">Member Name</th>
            <th className="p-4 text-left">Membership</th>
            <th className="p-4 text-left">Issued Date</th>
            <th className="p-4 text-left">Expiration Date</th>
            {/*<th className="p-4 text-left">Certificate</th>*/}
          </tr>
          </thead>
          <tbody className=" divide-white divide-y-[10px]">
          {currentCertificates.map((certificate) => (
            <tr className="bg-green/20" key={certificate._id}>
              <td className="text-left p-4">{certificate.ownerRegNumber}</td>
              <td className="text-left p-4">{certificate.certificateOwner}</td>
              <td className="text-left p-4">{certificate.ownerCategory}</td>
              <td className="text-left p-4">{certificate.issuedDate}</td>
              <td className="text-left p-4">{certificate.expirationDate}</td>
              {/*<td className="text-left p-4">*/}
              {/*  <a href={certificate.fileImage} target="_blank" rel="noreferrer" className="underline"> View </a>*/}
              {/*</td>*/}
            </tr>
          ))}
          </tbody>
        </table>
      </div>
      {currentCertificates.length === 0 && !isLoading && <div className="text-center mt-4 text-gray-500">No certificates found</div>}
      {isLoading && <div className='flex justify-center items-center text-xl mt-12 font-bold p-4 m-auto'><PulseLoader
        color="#000000"
        loading={true}
        size={10}
        aria-label="Loading Spinner"
        data-testid="loader"
      /></div>}
      {renderPagination()}
    </div>
  );
}

export default CertifiedMembersPage;